import httpClient from '~/lib/http-client';
import type { Chat, ChatListItem, SaveChatRequest } from '@shared/types/chat';

export async function apiGetChats(): Promise<ChatListItem[]> {
  const { data } = await httpClient.get<ChatListItem[]>('/api/chats');
  return data;
}

export interface ActiveGeneration {
  id: string;
  urlId: string | null;
  description: string | null;
}

/** The caller's single in-flight generation, if any (drives badges + toast). */
export async function apiGetActiveGeneration(): Promise<ActiveGeneration | null> {
  const { data } = await httpClient.get<{ running: ActiveGeneration | null }>('/api/chat/active');
  return data.running;
}

export async function apiGetChat(id: string): Promise<Chat> {
  const { data } = await httpClient.get<Chat>(`/api/chats/${id}`);
  return data;
}

export async function apiGetChatByUrlId(urlId: string): Promise<Chat> {
  const { data } = await httpClient.get<Chat>(`/api/chats/by-url/${urlId}`);
  return data;
}

export async function apiSaveChat(chat: SaveChatRequest): Promise<Chat> {
  const { data } = await httpClient.post<Chat>('/api/chats', chat);
  return data;
}

export async function apiDeleteChat(id: string): Promise<void> {
  await httpClient.delete(`/api/chats/${id}`);
}

export async function apiMigrateChats(chats: any[]): Promise<{ imported: number }> {
  const { data } = await httpClient.post<{ imported: number }>('/api/chats/migrate', chats);
  return data;
}

export async function apiGetLockedItems(chatId: string): Promise<any[]> {
  const { data } = await httpClient.get<any[]>(`/api/chats/${chatId}/locked-items`);
  return data;
}

export async function apiUpdateLockedItems(chatId: string, items: any[]): Promise<any[]> {
  const { data } = await httpClient.put<any[]>(`/api/chats/${chatId}/locked-items`, items);
  return data;
}

export async function cancelGeneration(chatId: string): Promise<void> {
  await httpClient.post('/api/chat/cancel', { chatId });
}

/**
 * Hand ask_user answers to the turn that's waiting on them. Resolves
 * `delivered: false` when no turn is parked (it timed out or already ended) —
 * the caller then falls back to sending the answers as a new message.
 */
export async function answerAskUser(
  chatId: string,
  answers: { questionId: string; question: string; answer: string }[],
  sessionKey?: string,
): Promise<{ delivered: boolean }> {
  const { data } = await httpClient.post<{ ok: true; delivered: boolean }>('/api/chat/ask-user/answer', {
    chatId,
    sessionKey,
    answers,
  });

  return { delivered: Boolean(data?.delivered) };
}

export interface Checkpoint {
  messageId: string;
  kind: string;
  createdAt: string;
  hasPreview: boolean;
}

export async function listCheckpoints(chatId: string): Promise<Checkpoint[]> {
  const { data } = await httpClient.get<{ checkpoints: Checkpoint[] }>(`/api/chats/${chatId}/checkpoints`);
  return data.checkpoints;
}

export interface RestoreCheckpointResult {
  restoreMessageId: string;
}

/** On failure the caller sees the raw axios error — read `err.response?.data?.code`
 *  (see `mapRestoreErrorCode`) for the server's structured error code. */
export async function restoreCheckpoint(chatId: string, messageId: string): Promise<RestoreCheckpointResult> {
  const { data } = await httpClient.post<RestoreCheckpointResult>(`/api/sandbox/${chatId}/restore-checkpoint`, {
    messageId,
  });
  return { restoreMessageId: data.restoreMessageId };
}

/** Maps the server's structured restore-checkpoint error `code` to a user-facing message. */
export function mapRestoreErrorCode(code?: string): string {
  switch (code) {
    case 'CHECKPOINT_GONE':
      return 'This version is no longer available';
    case 'TURN_IN_FLIGHT':
      return 'Stop the current generation first';
    case 'RESTORE_UNRECORDED':
      return 'Files were restored but saving the restore point failed — try restoring again';
    default:
      return 'Failed to restore this version';
  }
}
